/**
 * AdminContext
 *
 * Centralized context for managing client selection across admin pages.
 * Handles permission-based client scoping for APP_ADMIN and CLIENT_ADMIN users.
 *
 * APP_ADMIN users can select any client from a dropdown.
 * CLIENT_ADMIN users are automatically scoped to their own client.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useUser } from '../hooks/useUser';
import { listClients } from '../services/clientService';
import * as logger from '../utils/logger';

const AdminContext = createContext(undefined);

/**
 * AdminProvider Component
 *
 * Provides admin context to all child components. Should wrap all admin pages
 * in the application (typically added in AuthenticatedApp.js).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const AdminProvider = ({ children }) => {
  const { user } = useUser();

  // Permission state
  const isAppAdmin = user?.groupRole === 'APP_ADMIN';
  const isClientAdmin = user?.groupRole === 'CLIENT_ADMIN';

  // Client data state
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClientState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch all clients on mount
   */
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      setError(null);

      try {
        const clients = await listClients();
        setAvailableClients(clients);

        // Initialize selectedClient to user's client
        if (user?.client) {
          setSelectedClientState(user.client);
        }

        logger.debug('AdminContext initialized', {
          clientCount: clients.length,
          userClient: user?.client,
          isAppAdmin,
          isClientAdmin
        });
      } catch (err) {
        logger.error('Failed to fetch clients in AdminContext', {
          error: err,
          userId: user?.id
        });
        setError(err);

        // Fallback: still set selected client to user's client even on error
        if (user?.client) {
          setSelectedClientState(user.client);
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchClients();
    } else {
      setLoading(false);
    }
  }, [user?.id]); // Only re-fetch if user changes

  /**
   * Wrapped setSelectedClient that respects permissions
   * CLIENT_ADMIN users cannot change client, so this returns null for them
   */
  const setSelectedClient = useCallback(
    isAppAdmin
      ? (clientId) => {
          logger.debug('Client selection changed', {
            previousClient: selectedClient,
            newClient: clientId,
            userId: user?.id
          });
          setSelectedClientState(clientId);
        }
      : null,
    [isAppAdmin, selectedClient, user?.id]
  );

  /**
   * For CLIENT_ADMIN, filter availableClients to only their client
   * For APP_ADMIN, show all clients
   */
  const filteredAvailableClients = useMemo(() => {
    if (isClientAdmin && user?.client) {
      // Find the single client object for CLIENT_ADMIN
      const userClient = availableClients.find(c => c.clientId === user.client);
      return userClient ? [userClient] : [];
    }
    return availableClients;
  }, [isClientAdmin, user?.client, availableClients]);

  /**
   * For CLIENT_ADMIN, lock selectedClient to user.client
   * For APP_ADMIN, use the state-managed selectedClient
   */
  const effectiveSelectedClient = useMemo(() => {
    if (isClientAdmin && user?.client) {
      return user.client;
    }
    return selectedClient;
  }, [isClientAdmin, user?.client, selectedClient]);

  const value = useMemo(
    () => ({
      selectedClient: effectiveSelectedClient,
      setSelectedClient,
      isAppAdmin,
      isClientAdmin,
      availableClients: filteredAvailableClients,
      loading,
      error
    }),
    [
      effectiveSelectedClient,
      setSelectedClient,
      isAppAdmin,
      isClientAdmin,
      filteredAvailableClients,
      loading,
      error
    ]
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

/**
 * useAdminContext Hook
 *
 * Custom hook to access the AdminContext values.
 * Must be used within an AdminProvider.
 *
 * @returns {Object} Admin context values
 * @returns {string|null} selectedClient - Currently selected client ID
 * @returns {function|null} setSelectedClient - Client setter (null for CLIENT_ADMIN)
 * @returns {boolean} isAppAdmin - User has APP_ADMIN role
 * @returns {boolean} isClientAdmin - User has CLIENT_ADMIN role
 * @returns {Array} availableClients - List of available client objects
 * @returns {boolean} loading - Client list fetch status
 * @returns {Error|null} error - Client fetch error if any
 *
 * @throws {Error} If used outside AdminProvider
 *
 * @example
 * const { selectedClient, isAppAdmin } = useAdminContext();
 */
export const useAdminContext = () => {
  const context = useContext(AdminContext);

  if (context === undefined) {
    throw new Error('useAdminContext must be used within an AdminProvider');
  }

  return context;
};
